import type { AssistantCitationLink, CitationLinkTarget } from '@/lib/assistant-citations'
import { buildCitationLinkTargets } from '@/lib/assistant-citations'

const MARKDOWN_LINK_OR_IMAGE = /(\[[^\]]*\]\([^)]*\)|!\[[^\]]*\]\([^)]*\))/g

function isTableSeparator(line: string): boolean {
	return /^\|[\s\-:|]+\|$/.test(line.trim())
}

interface TextMatch {
	start: number
	end: number
	label: string
	href: string
}

function linkifyPlainText(text: string, targets: CitationLinkTarget[]): string {
	if (!text.trim() || !targets.length) return text

	const matches: TextMatch[] = []
	for (const { label, href } of targets) {
		let idx = 0
		while ((idx = text.indexOf(label, idx)) !== -1) {
			matches.push({ start: idx, end: idx + label.length, label, href })
			idx += label.length
		}
	}

	if (!matches.length) return text

	matches.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start))

	const selected: TextMatch[] = []
	let lastEnd = 0
	for (const match of matches) {
		if (match.start < lastEnd) continue
		selected.push(match)
		lastEnd = match.end
	}

	let out = ''
	let pos = 0
	for (const match of selected) {
		out += text.slice(pos, match.start)
		out += `[${match.label}](${match.href})`
		pos = match.end
	}
	out += text.slice(pos)
	return out
}

function linkifyLinePreservingMarkdown(line: string, targets: CitationLinkTarget[]): string {
	const parts = line.split(MARKDOWN_LINK_OR_IMAGE)
	return parts
		.map((part, index) => (index % 2 === 1 ? part : linkifyPlainText(part, targets)))
		.join('')
}

/**
 * Turn product/store/cabinet names in assistant prose into internal markdown links
 * using citation metadata from tool results.
 */
export function linkifyAssistantCitations(
	content: string,
	citations: AssistantCitationLink[],
): string {
	const targets = buildCitationLinkTargets(citations)
	if (!targets.length) return content

	let inCodeFence = false
	return content
		.split('\n')
		.map((line) => {
			const trimmed = line.trim()
			if (trimmed.startsWith('```')) {
				inCodeFence = !inCodeFence
				return line
			}
			if (inCodeFence || isTableSeparator(trimmed)) return line
			return linkifyLinePreservingMarkdown(line, targets)
		})
		.join('\n')
}
